import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { IPage } from '../../../models/page';
import useTranslations from '../../../hooks/useTranslations';
import { FieldTypes, IFormFields } from '../../../models/form';
import Form from '../../../components/Container/Form/Form';
import Alert from '../../../components/UI/Alert/Alert';
import BackButton from '../../../components/UI/BackButton/BackButton';
import usePermissionRedirect from '../../../hooks/usePermissionRedirect';
import useSiteOptions from '../../../hooks/useSiteOptions';
import { CustomerAssignmentsActions } from '../../../store/entities/customerAssignments/customerAssignments.actions';
import { CONSTANTS } from '../../../utils/constants';
import AddCustomerAssignmentsFromFile from './AddCustomerAssignmentsFromFile/AddCustomerAssignmentsFromFile';
import { IImportedAssignmentItem } from '../../../utils/customerAssignmentsExcel';
import EditableAssignmentsTable from './EditableAssignmentsTable';
import { customerAssignmentsInsertRangeValidation } from '../../../utils/formValidation/customerAssignmentsInsertRange';
import { SiteTypes } from '../../../models/pricingPlan';

const CustomerAssignmentsInsertRange = ({ permission, nextRoute }: IPage) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { rcTranslate } = useTranslations();
  usePermissionRedirect(permission, nextRoute);
  const siteList = useSiteOptions(SiteTypes.residential);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [assignments, setAssignments] = useState<IImportedAssignmentItem[]>([]);

  const fields: IFormFields = useMemo(() => {
    return {
      initialValues: {
        siteId: '',
        voucherExpiryDate: '',
        sBSubscriptionVoucherDurationInMonths: '',
        upgradeToSmartLockerVoucherDurationInMonths: '',
      },
      formData: [
        {
          type: FieldTypes.AUTOCOMPLETE,
          id: 'siteId',
          label: rcTranslate('customerAssignments.insertRange.fields.site'),
          items: siteList,
          props: { multiple: false },
        },
        {
          type: FieldTypes.DATE,
          id: 'voucherExpiryDate',
          label: rcTranslate(
            'customerAssignments.insertRange.fields.voucherExpiryDate'
          ),
          props: { getFullDayTime: true, numOfDays: 1 },
        },
        {
          type: FieldTypes.NUMBER,
          id: 'sBSubscriptionVoucherDurationInMonths',
          label: rcTranslate(
            'customerAssignments.insertRange.fields.subscriptionDuration'
          ),
        },
        {
          type: FieldTypes.NUMBER,
          id: 'upgradeToSmartLockerVoucherDurationInMonths',
          label: rcTranslate(
            'customerAssignments.insertRange.fields.upgradeDuration'
          ),
        },
        {
          type: FieldTypes.CHILDREN,
          id: 'assignmentsUploadWrapper',
          fields: [
            {
              type: FieldTypes.CHILDREN,
              id: 'assignmentsInner',
              fields: [
                {
                  type: FieldTypes.CHILDREN,
                  id: 'assignmentsUploadButton',
                  fields: [
                    {
                      type: FieldTypes.COMPONENT,
                      id: 'uploadAssignments',
                      label: '',
                      component: () => (
                        <>
                          <AddCustomerAssignmentsFromFile
                            setAssignments={setAssignments}
                          />
                          <EditableAssignmentsTable
                            assignments={assignments}
                            onChange={setAssignments}
                          />
                        </>
                      ),
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    } as IFormFields;
  }, [assignments, rcTranslate, siteList, setAssignments]);

  const onSubmit = async (values: any, setSubmitting: (v: boolean) => void) => {
    const body = {
      siteId: values.siteId,
      voucherExpiryDate: values.voucherExpiryDate || null,
      sBSubscriptionVoucherDurationInMonths:
        values.sBSubscriptionVoucherDurationInMonths
          ? values.sBSubscriptionVoucherDurationInMonths
          : null,
      upgradeToSmartLockerVoucherDurationInMonths:
        values.upgradeToSmartLockerVoucherDurationInMonths
          ? values.upgradeToSmartLockerVoucherDurationInMonths
          : null,
      usersData: assignments.map((a) => ({
        fullNameFromContractor: a.fullNameFromContractor,
        phoneNumber: a.phoneNumber,
        lockerNumber: a.lockerNumber || null,
        sBSubscriptionVoucher: Boolean(a.sBSubscriptionVoucher),
        upgradeToSmartLockerVoucher: Boolean(a.upgradeToSmartLockerVoucher),
      })),
    };
    const res: any = await dispatch(
      CustomerAssignmentsActions.insertCustomerAssignmentsRange(body)
    );
    setSubmitting(false);
    if (res?.added) {
      const addedCount = res.added?.length ?? 0;
      setAlertMessage(
        `${rcTranslate('customerAssignments.insertRange.success')}: ${addedCount} ${rcTranslate(
          'customerAssignments.insertRange.added'
        )}`
      );
      setShowAlert(true);
      setTimeout(() => setShowAlert(false), 15000);
      navigate(
        '/' +
          CONSTANTS.ROUTES.CUSTOMER_ASSIGNMENTS_MANAGEMENT.BASE +
          '/' +
          CONSTANTS.ROUTES.CUSTOMER_ASSIGNMENTS_MANAGEMENT.CHILDREN
            .CUSTOMER_ASSIGNMENTS_LIST
      );
    }
  };

  return (
    <>
      <BackButton />
      <Form
        fields={fields}
        formValidation={customerAssignmentsInsertRangeValidation}
        onSubmit={onSubmit}
        submitButtonTitle={rcTranslate(
          'customerAssignments.insertRange.submitButtonTitle'
        )}
        formClassName="formWrapper--fullWidth"
      />
      {showAlert && <Alert message={alertMessage} color="success" />}
    </>
  );
};

export default CustomerAssignmentsInsertRange;
